import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { getStoreContext, getStoreNamespacedKey } from '../utils/storeUtils';

const CustomerContext = createContext();

export const useCustomer = () => useContext(CustomerContext);

export const CustomerProvider = ({ children }) => {
    const location = useLocation();
    const currentContext = getStoreContext(location.pathname);
    const [activeContext, setActiveContext] = useState(currentContext);

    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Temporary state for checkout pre-fill (when smart login finds a match but not fully logged in)
    const [smartLoginData, setSmartLoginData] = useState(null);

    useEffect(() => {
        const loadCustomer = async () => {
            const storageKey = getStoreNamespacedKey('auth_token', currentContext);
            const token = localStorage.getItem(storageKey);
            if (token) {
                try {
                    // Set token header (api utility should handle this if configured, or we set it manually)
                    // Assuming api interceptor reads from localStorage or we set default header
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                    const { data } = await api.get('/customers/me');
                    setCustomer(data);
                } catch (err) {
                    console.error("Failed to load customer", err);
                    localStorage.removeItem(storageKey);
                    delete api.defaults.headers.common['Authorization'];
                    setCustomer(null);
                }
            } else {
                delete api.defaults.headers.common['Authorization'];
                setCustomer(null);
            }
            setActiveContext(currentContext);
            setLoading(false);
        };

        loadCustomer();
    }, [currentContext]);

    const login = async (store_id, email, password) => {
        try {
            setError(null);
            const { data } = await api.post('/customers/login', { store_id, email, password });
            const storageKey = getStoreNamespacedKey('auth_token', currentContext);
            localStorage.setItem(storageKey, data.token);
            api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            setCustomer(data);
            return data;
        } catch (err) {
            setError(err.response?.data?.error || 'Login failed');
            throw err;
        }
    };

    const loginWithToken = async (token) => {
        try {
            const storageKey = getStoreNamespacedKey('auth_token', currentContext);
            localStorage.setItem(storageKey, token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const { data } = await api.get('/customers/me');
            setCustomer(data);
            return data;
        } catch (err) {
            console.error("Token login failed", err);
            const storageKey = getStoreNamespacedKey('auth_token', currentContext);
            localStorage.removeItem(storageKey);
            delete api.defaults.headers.common['Authorization'];
            setCustomer(null);
        }
    };

    const register = async (store_id, customerData) => {
        try {
            setError(null);
            const { data } = await api.post('/customers/register', { store_id, ...customerData });
            const storageKey = getStoreNamespacedKey('auth_token', currentContext);
            localStorage.setItem(storageKey, data.token);
            api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            setCustomer(data);
            return data;
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed');
            throw err;
        }
    };

    const logout = () => {
        const storageKey = getStoreNamespacedKey('auth_token', currentContext);
        localStorage.removeItem(storageKey);
        delete api.defaults.headers.common['Authorization'];
        setCustomer(null);
        setSmartLoginData(null);
    };

    const checkExists = async (store_id, email, phone) => {
        try {
            const { data } = await api.post('/customers/check-exists', { store_id, email, phone });
            if (data.exists) {
                setSmartLoginData(data); // Store for pre-fill
            }
            return data;
        } catch (err) {
            console.error("Check exists failed", err);
            return { exists: false };
        }
    };

    const updateProfile = async (updates) => {
        try {
            const { data } = await api.put('/customers/me', updates);
            setCustomer(data);
            return data;
        } catch (err) {
            setError(err.response?.data?.error || 'Update failed');
            throw err;
        }
    };

    const getOrders = async () => {
        try {
            const { data } = await api.get('/customers/orders');
            return data;
        } catch (err) {
            console.error("Failed to fetch orders", err);
            return [];
        }
    };

    const updateAddresses = async (addresses) => {
        try {
            const { data } = await api.put('/customers/addresses', { addresses });
            setCustomer({ ...customer, addresses: data });
            return data;
        } catch (err) {
            console.error("Failed to update addresses", err);
            throw err;
        }
    };

    return (
        <CustomerContext.Provider value={{
            customer,
            loading,
            error,
            smartLoginData,
            login,
            loginWithToken,
            register,
            logout,
            checkExists,
            updateProfile,
            getOrders,
            updateAddresses
        }}>
            {children}
        </CustomerContext.Provider>
    );
};
